import { NodeSelection } from "@tiptap/pm/state"
import { Slice } from "@tiptap/pm/model"

import { parseInertHTML } from "@/tiptap/core/inert-html"

const EDITOR_IMAGE_UI_SELECTOR = [
  ".tiptap-image-handle",
  ".tiptap-image-content",
  ".tiptap-image-container",
  ".tiptap-image",
  ".react-renderer.node-image",
].join(", ")

function isSelectedImage(selection) {
  return selection instanceof NodeSelection && selection.node?.type?.name === "image"
}

function imageFilesFromClipboard(event) {
  const items = Array.from(event.clipboardData?.items || [])
  const files = items
    .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
    .map((item) => item.getAsFile())
    .filter(Boolean)

  if (files.length > 0) return files

  return Array.from(event.clipboardData?.files || []).filter((file) =>
    file.type.startsWith("image/"),
  )
}

function altTextFromImageSrc(src) {
  if (!src) return null

  const path = src.split(/[?#]/)[0]
  const alt = path.split("/").filter(Boolean).pop()
  return alt || null
}

function plainTextForSelection(text, selection) {
  if (text) return text
  if (isSelectedImage(selection)) {
    return relativeSrcForSameOriginURL(selection.node.attrs.src || "")
  }

  return ""
}

export function relativeSrcForSameOriginURL(src) {
  if (!src || typeof window === "undefined") return src
  if (!/^https?:\/\//i.test(src)) return src

  try {
    const url = new URL(src)
    if (url.origin !== window.location.origin) return src

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return src
  }
}

// Same-origin `/file/{slug}` URLs must be stored as a relative path. Shared
// articles and portal views rewrite `/file/` to their own routes; an absolute
// `https://this-host/file/{slug}` src skips that rewrite and 401s.
export function normalizeFirstPartyFileSrc(src) {
  if (!src || typeof src !== "string") return src

  const relative = relativeSrcForSameOriginURL(src)
  const pathname = relative.split(/[?#]/)[0]

  return /^\/file\/[^/]+$/.test(pathname) ? relative : src
}

// Sidebar photo cards link to the lightbox page (/photos/:slug). The actual
// image file is served at /photo/:slug. Rewrite so dropped/pasted <img> tags render.
// Absolute URLs are only rewritten when they match this origin.
export function normalizeFirstPartyImageSrc(src) {
  if (!src || typeof src !== "string") return src

  let path = src
  if (/^https?:\/\//i.test(src) || src.startsWith("//")) {
    try {
      const url = new URL(src, typeof window !== "undefined" ? window.location.href : undefined)
      if (typeof window === "undefined" || url.origin !== window.location.origin) {
        return src
      }
      path = url.pathname
    } catch {
      return src
    }
  }

  const [pathname] = path.split(/[?#]/)
  if (!pathname.startsWith("/photos/")) return src

  return `/photo/${pathname.slice("/photos/".length)}`
}

function normalizeClipboardImageSources(html) {
  if (!html || typeof document === "undefined") return html

  const container = parseInertHTML(html)

  container.querySelectorAll("img[src]").forEach((image) => {
    image.setAttribute(
      "src",
      normalizeFirstPartyImageSrc(relativeSrcForSameOriginURL(image.getAttribute("src"))),
    )
  })

  return container.innerHTML
}

export function serializeSelectionForClipboard(view, selection) {
  const { dom, text } = view.serializeForClipboard(selection.content())

  return {
    html: normalizeClipboardImageSources(dom.innerHTML),
    text: plainTextForSelection(text, selection),
  }
}

function defer(callback) {
  window.setTimeout(callback, 0)
}

function deleteSelectedImage(view, selection) {
  const { from, to } = selection

  defer(() => {
    if (view.isDestroyed) return

    const node = view.state.doc.nodeAt(from)
    if (node?.type?.name !== "image") return

    view.dispatch(
      view.state.tr
        .delete(from, Math.min(to, view.state.doc.content.size))
        .scrollIntoView()
        .setMeta("uiEvent", "cut"),
    )
  })
}

export function handleImageSelectionCopy(view, event, { cut = false } = {}) {
  const { selection } = view.state

  if (!isSelectedImage(selection) || !event.clipboardData) {
    return false
  }

  const { html, text } = serializeSelectionForClipboard(view, selection)

  event.preventDefault()
  event.clipboardData.clearData()
  event.clipboardData.setData("text/html", html)
  event.clipboardData.setData("text/plain", text)

  if (cut) {
    deleteSelectedImage(view, selection)
  }

  return true
}

function canUseAsyncClipboard() {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.clipboard?.write === "function" &&
    typeof ClipboardItem !== "undefined"
  )
}

// Fallback clipboard write for contexts without navigator.clipboard.write
// (plain-HTTP origins, older Firefox). Mirrors prosemirror-view's own
// captureCopy fallback: stage the serialized HTML in a hidden, selectable
// element OUTSIDE the editor, select it, and copy that. A selection over
// normal selectable content always copies — unlike the editor's node
// selection, which Chromium refuses to fire copy/cut events for
// (https://crbug.com/1233054).
function copyHTMLViaHiddenSelection(view, html) {
  const doc = view.dom?.ownerDocument || document
  const win = doc.defaultView || window

  if (typeof doc.execCommand !== "function") return false

  const parent = view.dom?.parentNode || doc.body
  if (!parent) return false
  const domSelection = win.getSelection()
  if (!domSelection) return false

  const wrap = parent.appendChild(doc.createElement("div"))
  wrap.style.cssText = "position: fixed; left: -10000px; top: 10px"
  wrap.setAttribute("contenteditable", "true")
  wrap.innerHTML = html

  const range = doc.createRange()
  range.selectNodeContents(wrap)
  view.dom?.blur?.()
  domSelection.removeAllRanges()
  domSelection.addRange(range)

  let succeeded = false
  try {
    succeeded = doc.execCommand("copy")
  } catch {
    succeeded = false
  }

  win.setTimeout(() => {
    if (wrap.parentNode) wrap.parentNode.removeChild(wrap)
    view.focus()
  }, 50)

  return succeeded
}

// Chromium does not fire copy/cut clipboard events when the DOM selection
// wraps a non-editable, non-selectable node view (https://crbug.com/1233054),
// so the DOM-event handlers above never run and the browser copies/cuts the
// raw node-view DOM natively — corrupting React-managed DOM on cut. Handling
// the keyboard shortcut directly avoids relying on the browser-fired event.
export function handleImageSelectionClipboardShortcut(view, { cut = false } = {}) {
  const { selection } = view.state

  if (!isSelectedImage(selection)) return false

  const { html, text } = serializeSelectionForClipboard(view, selection)

  if (canUseAsyncClipboard()) {
    const clipboardItem = new ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([text || ""], { type: "text/plain" }),
    })

    navigator.clipboard
      .write([clipboardItem])
      .then(() => {
        if (cut) deleteSelectedImage(view, selection)
      })
      .catch(() => {
        const copied = copyHTMLViaHiddenSelection(view, html)
        if (cut && copied) deleteSelectedImage(view, selection)
      })

    return true
  }

  const copied = copyHTMLViaHiddenSelection(view, html)

  if (cut) {
    if (copied) {
      deleteSelectedImage(view, selection)
    }

    // Always consume the cut shortcut: letting the browser cut natively rips
    // the node-view DOM out from under React and blanks the page.
    return true
  }

  return copied
}

// Selecting an image applies the crbug.com/1233054 CSS workaround
// (-webkit-user-modify: read-write on the node view) so clipboard events fire
// at all. In Chromium that also makes every element inside the node view
// report isContentEditable, so TipTap's NodeView.stopEvent treats the paste
// event as node-view-internal input and stops it before ProseMirror's
// eventBelongsToView check — the paste never reaches the editor and is never
// preventDefault-ed. Chromium then pastes natively into the editable node
// view DOM, dumping the raw clipboard text into the image's caption content.
// Handle paste for image node selections in the capture phase, before the
// node view can swallow the event, and feed it through the normal
// ProseMirror paste pipeline.
export function handleImageSelectionPaste(view, event) {
  const { selection } = view.state

  if (!view.editable || !isSelectedImage(selection) || !event.clipboardData) {
    return false
  }

  event.preventDefault()
  event.stopPropagation()

  const html = event.clipboardData.getData("text/html")
  const text = event.clipboardData.getData("text/plain")

  if (html) {
    view.pasteHTML(html, event)
  } else if (text) {
    view.pasteText(text, event)
  } else {
    // File-only clipboards (e.g. OS screenshots) never reach doPaste, so run
    // the handlePaste props (image upload) directly.
    view.someProp("handlePaste", (handler) => handler(view, event, Slice.empty))
  }

  return true
}

function insertUploadedImages(view, selectionRange, uploadedImages) {
  const imageType = view.state.schema.nodes.image
  if (!imageType || uploadedImages.length === 0) return

  const imageNodes = uploadedImages.map(({ url }) => {
    const alt = altTextFromImageSrc(url)
    return imageType.create({ src: url, ...(alt ? { alt } : {}) })
  })
  const from = Math.min(selectionRange.from, view.state.doc.content.size)
  const to = Math.max(from, Math.min(selectionRange.to, view.state.doc.content.size))
  let tr = view.state.tr.replaceRangeWith(from, to, imageNodes[0])
  let insertPos = from + imageNodes[0].nodeSize

  imageNodes.slice(1).forEach((node) => {
    tr = tr.insert(insertPos, node)
    insertPos += node.nodeSize
  })

  view.dispatch(tr.scrollIntoView().setMeta("uiEvent", "paste"))
}

export function handleClipboardImagePaste(view, event, { imageUpload } = {}) {
  // Excel (and Word) put an HTML <table> and a rendered bitmap on the same
  // clipboard. Prefer the table so paste becomes an editable grid instead of
  // an uploaded screenshot of the cells.
  const html = event.clipboardData?.getData("text/html") || ""
  if (/<table[\s>]/i.test(html)) return false

  const files = imageFilesFromClipboard(event)

  if (files.length === 0 || !imageUpload?.upload) {
    return false
  }

  const limit = imageUpload.limit ?? files.length
  const filesToUpload = files.slice(0, limit)
  const selectionRange = {
    from: view.state.selection.from,
    to: view.state.selection.to,
  }

  event.preventDefault()

  Promise.all(filesToUpload.map((file) => imageUpload.upload(file)))
    .then((urls) => {
      const uploadedImages = urls
        .map((url) => (url ? { url } : null))
        .filter(Boolean)

      insertUploadedImages(view, selectionRange, uploadedImages)
    })
    .catch((error) => {
      imageUpload.onError?.(error instanceof Error ? error : new Error("Upload failed"))
    })

  return true
}

// Chrome serializes computed theme styles onto copied HTML (Hudini chat, dark
// mode UI). Matching a swatch hex is not enough — banners and other UI can
// land on the same RGB. On external paste, keep a color/background only when
// it is an editor token or a <mark>. Google Docs is the exception: it uses
// plain hex/rgb colors, so we keep those unless they are a default Docs color
// (body black, heading gray). In-editor slices (`data-pm-slice`) and saved
// article HTML keep whatever is already stored.
const EDITOR_HIGHLIGHT_TOKEN = /var\(\s*--tt-color-highlight-/
const EDITOR_TEXT_COLOR_TOKEN = /var\(\s*--tt-color-text-/
// Google Docs selector
const GOOGLE_DOCS_SELECTOR = '[id^="docs-internal-guid-"]'
// Default text colors for Google Docs. We need to ignore these when pasting into the editor because they can be preserved even in dark mode
const DEFAULT_TEXT_COLORS = new Set([
  "#000",
  "#000000",
  "black",
  "rgb(0,0,0)",
  "rgba(0,0,0,1)",
  "windowtext",
  // Google Docs heading defaults (Heading 3 #434343, Heading 4+ / subtitle #666666)
  "#434343",
  "rgb(67,67,67)",
  "rgba(67,67,67,1)",
  "#666666",
  "#666",
  "rgb(102,102,102)",
  "rgba(102,102,102,1)",
])
const DEFAULT_BACKGROUND_COLORS = new Set([
  "transparent",
  "inherit",
  "initial",
  "unset",
  "none",
  "rgba(0,0,0,0)",
  "#fff",
  "#ffffff",
  "white",
  "rgb(255,255,255)",
  "rgba(255,255,255,1)",
])

function styleHaystack(element) {
  return [
    element.getAttribute("style"),
    element.getAttribute("data-background-color"),
    element.getAttribute("data-color"),
  ].join(" ")
}

function isExplicitHighlight(element) {
  return element.closest("mark") || EDITOR_HIGHLIGHT_TOKEN.test(styleHaystack(element))
}

function isExplicitTextColor(element) {
  return EDITOR_TEXT_COLOR_TOKEN.test(styleHaystack(element))
}

function normalizeCssColor(value) {
  return value.trim().toLowerCase().replace(/\s+/g, "")
}

function isDefaultTextColor(value) {
  if (!value) return true

  return DEFAULT_TEXT_COLORS.has(normalizeCssColor(value))
}

function isDefaultBackgroundColor(value) {
  if (!value) return true

  return DEFAULT_BACKGROUND_COLORS.has(normalizeCssColor(value))
}

// Google Docs highlights are <span style="background-color:…">. TipTap only
// parses highlights from <mark>, and the span mark strips background-color, so
// convert a non-default span background into a real highlight mark first.
function convertGoogleDocsHighlights(container) {
  container.querySelectorAll("span").forEach((span) => {
    const background = span.style?.backgroundColor
    if (isDefaultBackgroundColor(background)) return

    span.style.removeProperty("background-color")
    span.style.removeProperty("background")
    if (!span.getAttribute("style")?.trim()) {
      span.removeAttribute("style")
    }

    const mark = container.ownerDocument.createElement("mark")
    mark.setAttribute("data-color", background)
    mark.style.backgroundColor = background
    mark.append(...Array.from(span.childNodes))
    span.append(mark)
  })
}

function stripComputedPasteStyles(element, { preserveTextColors = false } = {}) {
  if (!isExplicitHighlight(element)) {
    if (element.style) {
      element.style.removeProperty("background-color")
      element.style.removeProperty("background")
    }
    element.removeAttribute("data-background-color")
  }

  const keepTextColor =
    isExplicitTextColor(element) ||
    (preserveTextColors && !isDefaultTextColor(element.style?.color || element.getAttribute("color")))

  if (!keepTextColor) {
    if (element.style) {
      element.style.removeProperty("color")
      element.style.removeProperty("-webkit-text-fill-color")
    }
    element.removeAttribute("color")
  } else if (element.style) {
    element.style.removeProperty("-webkit-text-fill-color")
  }

  if (element.style && !element.getAttribute("style")?.trim()) {
    element.removeAttribute("style")
  }
}

const GOOGLE_DOCS_BLANK_LINE_MEDIA_SELECTOR = "img, picture, video, iframe, svg, object, embed"
const GOOGLE_DOCS_BLOCK_ANCESTOR_SELECTOR = "p, h1, h2, h3, h4, h5, h6, li, td, th, blockquote, pre, figure"
const GOOGLE_DOCS_INLINE_WRAPPER_TAGS = new Set([
  "A",
  "B",
  "EM",
  "FONT",
  "I",
  "MARK",
  "S",
  "SMALL",
  "SPAN",
  "STRIKE",
  "STRONG",
  "SUB",
  "SUP",
  "U",
])

// Google Docs blank lines are <p><br></p>. ProseMirror parses the <br> as a
// hardBreak and still renders its own trailing filler break, so the gap is
// twice as tall as in Docs. Drop one <br> per blank paragraph (n → n−1) so a
// single Docs blank line becomes an empty paragraph — one line in the editor
// and in view mode. Bare block-level <br>s (sometimes emitted between Docs
// blocks) are rewritten to the same empty <p> so they do not get wrapped into
// a hardBreak paragraph later.
function isIgnorableBlankLineText(node) {
  return node.nodeType === Node.TEXT_NODE && node.textContent.replace(/\u00a0/g, "").trim() === ""
}

function isGoogleDocsBlankLineParagraph(paragraph) {
  if (paragraph.textContent.replace(/\u00a0/g, "").trim() !== "") return false
  if (!paragraph.querySelector("br")) return false

  return !paragraph.querySelector(GOOGLE_DOCS_BLANK_LINE_MEDIA_SELECTOR)
}

function collapseGoogleDocsBlankLineParagraphs(container) {
  container.querySelectorAll("p").forEach((paragraph) => {
    if (!isGoogleDocsBlankLineParagraph(paragraph)) return

    paragraph.querySelector("br")?.remove()

    if (!paragraph.querySelector("br")) {
      paragraph.replaceChildren()
    }
  })
}

function isAppleInterchangeNewline(br) {
  return br.classList.contains("Apple-interchange-newline")
}

function isBareGoogleDocsBlockBreak(br) {
  if (isAppleInterchangeNewline(br)) return false

  return !br.closest(GOOGLE_DOCS_BLOCK_ANCESTOR_SELECTOR)
}

function wrapperContainsOnly(parent, child) {
  return Array.from(parent.childNodes).every((node) => node === child || isIgnorableBlankLineText(node))
}

function outermostInlineWrapperForBareBreak(br) {
  let node = br

  while (
    node.parentElement &&
    GOOGLE_DOCS_INLINE_WRAPPER_TAGS.has(node.parentElement.tagName) &&
    wrapperContainsOnly(node.parentElement, node)
  ) {
    node = node.parentElement
  }

  return node
}

function isGoogleDocsBlockSibling(node) {
  return (
    node.nodeType === Node.ELEMENT_NODE &&
    node.matches(`${GOOGLE_DOCS_BLOCK_ANCESTOR_SELECTOR}, ol, ul, div, table, hr`)
  )
}

function isInlineHardBreakSibling(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return !isIgnorableBlankLineText(node)
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return false
  if (isGoogleDocsBlockSibling(node)) return false
  if (node.id?.startsWith("docs-internal-guid-")) return false

  return node.textContent.replace(/\u00a0/g, "").trim() !== ""
}

function isInlineHardBreak(br) {
  return Array.from(br.parentNode?.childNodes || []).some(
    (node) => node !== br && isInlineHardBreakSibling(node)
  )
}

function collapseGoogleDocsBareBreaks(container) {
  Array.from(container.querySelectorAll("br"))
    .filter(isBareGoogleDocsBlockBreak)
    .forEach((br) => {
      const node = outermostInlineWrapperForBareBreak(br)
      // Only skip `first<br>second` (or styled spans around a soft break).
      // A bare <br> beside blocks or after the Docs <b> wrapper is a blank
      // line — ProseMirror would wrap it as <p><br></p> (double height).
      if (node === br && isInlineHardBreak(br)) return

      const emptyParagraph = container.ownerDocument.createElement("p")
      node.replaceWith(emptyParagraph)
    })
}

// Google Docs list items set `white-space: pre`. The editor hides that because
// ProseMirror has no whitespace text nodes, but view mode serializes the HTML
// with newlines between blocks. Those newlines then render literally, so the
// marker sits on its own line. Strip only exact `pre` — leave `pre-wrap` on
// text spans alone.
function stripGoogleDocsPreWhitespace(container) {
  container.querySelectorAll("li[style]").forEach((element) => {
    if (element.style.whiteSpace !== "pre") return

    element.style.removeProperty("white-space")
    if (!element.getAttribute("style")?.trim()) {
      element.removeAttribute("style")
    }
  })
}

function stripExternalPasteStyles(container) {
  if (container.querySelector("[data-pm-slice]")) return

  const isGoogleDocsPaste = Boolean(container.querySelector(GOOGLE_DOCS_SELECTOR))

  if (isGoogleDocsPaste) {
    convertGoogleDocsHighlights(container)
  }

  container
    .querySelectorAll("[style], [data-background-color], [data-color], [color]")
    .forEach((element) => stripComputedPasteStyles(element, { preserveTextColors: isGoogleDocsPaste }))

  if (isGoogleDocsPaste) {
    collapseGoogleDocsBlankLineParagraphs(container)
    collapseGoogleDocsBareBreaks(container)
    stripGoogleDocsPreWhitespace(container)
  }
}

export function sanitizeEditorClipboardHTML(html, { stripExternalBackgrounds = false } = {}) {
  if (!html || typeof document === "undefined") return html

  const container = parseInertHTML(html)

  if (stripExternalBackgrounds) {
    stripExternalPasteStyles(container)
  }

  container.querySelectorAll(".tiptap-image-content").forEach((content) => {
    const image = content.querySelector("img[src]")

    if (image) {
      image.removeAttribute("class")
      image.removeAttribute("contenteditable")
      image.removeAttribute("draggable")
      content.replaceWith(image)
    } else {
      content.remove()
    }
  })

  container.querySelectorAll(".tiptap-image-handle").forEach((handle) => {
    handle.remove()
  })

  container
    .querySelectorAll(".tiptap-image-container, .tiptap-image, .react-renderer.node-image")
    .forEach((wrapper) => {
      wrapper.replaceWith(...Array.from(wrapper.childNodes))
    })

  const sanitizedHTML = container.querySelector(EDITOR_IMAGE_UI_SELECTOR)
    ? sanitizeEditorClipboardHTML(container.innerHTML, { stripExternalBackgrounds })
    : container.innerHTML

  return normalizeClipboardImageSources(sanitizedHTML)
}
